import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import { CmsLayout } from 'modules/cms/layout/CmsLayout';
import { LoadingPage } from 'modules/main/pages/Loading';
import { NotFoundPage } from 'modules/main/pages/NotFound';
import { MainStoreState } from 'modules/main/store';
import { fetchPage } from 'modules/main/store/ducks/contents';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useAmpContext } from 'utils/AmpContext';
import { hasParam } from 'utils/url';

import { CmsAmpContentPage } from './CmsAmpContentPage';
import { CmsContentPage } from './CmsContentPage';

type Props = {
  slug: string;
  amp: boolean;
};

export default function CmsContentPageContainer({ slug, amp }: Props) {
  const dispatch = useMainDispatch();
  const location = useLocation<{ subsite?: string }>();
  const requestIsAmp = useAmpContext();

  const { contents, mainSearch } = useSelector((state: MainStoreState) => ({
    contents: state.contents,
    mainSearch: state.mainSearch,
  }));

  const { isLandingPage } = mainSearch || false;
  // @ts-expect-error TS(2339): Property 'byUrl' does not exist on type 'never'.
  const contentObj = contents.byUrl[slug] || {};

  const { content } = contentObj;
  const pageState = contentObj.state || 'UNFETCHED';

  const hasHomePage = content?.subsite && Boolean(content.subsite.homePage);
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const heroSearch = content && content.heroSearch;

  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const onlySearchResults = heroSearch && heroSearch.onlyResults;
  const onlyArticleResults =
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    content && content.onlySearchResults && hasHomePage;

  useEffect(() => {
    if (!amp) {
      dispatch(fetchPage(slug));
    }
  }, [slug, amp, dispatch]);

  if (hasParam(location.search, 'saved')) {
    return (
      <Navigate
        to={`${location.pathname}${
          location.search ? `${location.search}&` : '?'
        }`}
      />
    );
  }

  if (
    onlySearchResults ||
    isLandingPage ||
    (onlyArticleResults && hasHomePage)
  ) {
    let qs = onlyArticleResults ? '?pq=' : '?q=';
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (location.search && location.search.includes('?q=')) {
      qs = location.search;
    } else if (location.search) {
      qs = `${location.search}&q=`;
    }

    return <Navigate to={`${location.pathname}${qs}`} replace />;
  }

  if (['FETCHING', 'UNFETCHED'].includes(pageState)) {
    if (amp) {
      // It's an AMP URL, but content is not already available.
      // Meaning it's disabled for that page.
      return <NotFoundPage />;
    }

    return <LoadingPage />;
  }

  if (content && contentObj.type === 'CMS_PAGE') {
    return (
      <CmsLayout content={content}>
        {requestIsAmp ? (
          <CmsAmpContentPage content={content} searchMode={false} />
        ) : (
          <CmsContentPage content={content} />
        )}
      </CmsLayout>
    );
  }

  return <NotFoundPage />;
}
